// Copyright (c) Ping. All rights reserved.
//
// Per-conversation serialization + pre-commit sync for MLS group membership
// commits (add/remove members). Two commits racing on the SAME group — a
// double-click, the "Add member" button overlapping the share-to-group path, or
// a retry overlapping its original — would each craft a Commit at the same MLS
// epoch; the server accepts the first and rejects the rest with `epoch_advanced`
// (HTTP 409). With the send-then-merge SDK that rejection rolls back cleanly, but
// it's still a wasted round-trip and a user-visible error. Chaining commits per
// conversation removes that self-inflicted race entirely.

import { log } from '@core/log/log';

/** In-flight commit chain per conversation id. */
const queues = new Map<string, Promise<unknown>>();

/**
 * Run `fn` after any in-flight membership commit for `conversationId` settles, so
 * commits on the same group never overlap. Errors are isolated: a failed op
 * doesn't break the chain for the next caller, and the rejection still surfaces
 * to THIS caller.
 */
export function runSerializedCommit<T>(
  conversationId: string,
  fn: () => Promise<T>,
): Promise<T> {
  const prev = queues.get(conversationId) ?? Promise.resolve();
  // Run regardless of whether the previous op resolved or rejected.
  const next = prev.then(fn, fn);
  // Store a non-rejecting tail so the chain survives a failed op.
  queues.set(
    conversationId,
    next.then(
      () => undefined,
      () => undefined,
    ),
  );
  return next;
}

/** Minimal shape of the SDK client's optional catch-up sync. */
interface SyncableClient {
  readonly syncConversations?: () => Promise<unknown>;
}

/**
 * Fast-forward local MLS state to the server's latest epoch before crafting a
 * Commit, so the Commit isn't built at a stale epoch (the "local behind the
 * server" → 409 cause, e.g. another admin committed while we were idle).
 * Best-effort — a sync failure must not block the membership change.
 */
export async function syncBeforeCommit(client: unknown): Promise<void> {
  const sync = (client as SyncableClient).syncConversations;
  if (typeof sync !== 'function') return;
  try {
    await sync.call(client);
  } catch (err) {
    log.warn({ event: 'groupCommit.syncBeforeCommit.failed', error: (err as Error).message });
  }
}
